import React from 'react';

import { Loader } from 'lucide-react';
import { toast } from 'react-toastify';

import { AddedIcon, AddIcon, CopyIcon, InstagramIcon, LinkedInIcon, Website } from '#/assets/svgs';
import {
    Avatar,
    AvatarFallback,
    AvatarImage,
    Button,
    Table,
    TableBody,
    TableCell,
    TableHeader,
    TableRow,
} from '#/components/ui';
import { COMPANY_DECISION_MAKERS_TABLE_HEADERS, COMPANY_SHEET_COL_GROUP } from '#/utils';
import { SortableTableHeader } from '.';

const CompanySheetTable = ({ data, isLoading, handleAddToContacts, setQueryParams, addToContactLoading }) => (
    <div className='overflow-x-auto border rounded-lg'>
        <h1 className='p-5'>Decision Makers</h1>
        <Table className='w-full table-fixed'>
            {COMPANY_SHEET_COL_GROUP}
            <TableHeader className='bg-white sticky top-0 z-10'>
                <SortableTableHeader headers={COMPANY_DECISION_MAKERS_TABLE_HEADERS} setQueryParams={setQueryParams} />
            </TableHeader>
        </Table>

        <div className='h-full overflow-y-auto pl-2'>
            <Table className='w-full table-fixed'>
                {COMPANY_SHEET_COL_GROUP}
                <TableBody className='w-full '>
                    {isLoading ? (
                        <TableRow>
                            <TableCell colSpan={5} className='h-[300px] text-center'>
                                <Loader className='mx-auto animate-spin text-muted-foreground' />
                            </TableCell>
                        </TableRow>
                    ) : data?.length > 0 ? (
                        data?.map(decision_maker => (
                            <TableRow
                                className='hover:bg-transparent cursor-default border-b h-[72px]'
                                key={decision_maker?.id}
                            >
                                <TableCell className='h-[72px] flex items-center gap-2 '>
                                    <Avatar className='w-8 h-8'>
                                        <AvatarImage
                                            src={decision_maker?.profilePicture}
                                            alt={decision_maker?.firstName}
                                        />
                                        <AvatarFallback className='bg-gray-200 text-gray500'>
                                            {decision_maker?.firstName?.charAt(0)}
                                            {decision_maker?.lastName?.charAt(0)}
                                        </AvatarFallback>
                                    </Avatar>
                                    <div className='flex flex-col max-w-[200px] '>
                                        <span className='break-words whitespace-normal text-gray900 text-sm font-medium capitalize'>
                                            {decision_maker?.firstName} {decision_maker?.lastName}
                                        </span>
                                        {decision_maker?.email && (
                                            <div className='flex flex-row items-center gap-2'>
                                                <span className='text-sm text-gray500 font-normal max-w-[200px] truncate'>
                                                    {decision_maker?.email}
                                                </span>
                                                <CopyIcon
                                                    className='cursor-pointer'
                                                    onClick={() => {
                                                        const email = decision_maker?.email || '';
                                                        navigator.clipboard.writeText(email).then(() => {
                                                            toast.success('Email copied to clipboard!');
                                                        });
                                                    }}
                                                />
                                            </div>
                                        )}
                                    </div>
                                </TableCell>

                                <TableCell className='whitespace-normal break-words pl-3 text-gray500'>
                                    {decision_maker?.title || '-'}
                                </TableCell>

                                <TableCell className='pl-3' onClick={e => e.stopPropagation()}>
                                    <div className='flex flex-row items-center gap-2'>
                                        {decision_maker?.websiteUrl && (
                                            <a
                                                href={decision_maker?.websiteUrl}
                                                target='_blank'
                                                rel='noopener noreferrer'
                                                className='inline-flex items-center justify-center'
                                            >
                                                <Website />
                                            </a>
                                        )}
                                        {decision_maker?.linkedinUrl && (
                                            <a
                                                href={decision_maker?.linkedinUrl}
                                                target='_blank'
                                                rel='noopener noreferrer'
                                                className='inline-flex items-center justify-center'
                                            >
                                                <LinkedInIcon />
                                            </a>
                                        )}
                                        {decision_maker?.instagramUrl && (
                                            <a
                                                href={decision_maker?.instagramUrl}
                                                target='_blank'
                                                rel='noopener noreferrer'
                                                className='inline-flex items-center justify-center'
                                            >
                                                <InstagramIcon />
                                            </a>
                                        )}
                                    </div>
                                </TableCell>
                                <TableCell className='pl-3'>
                                    <Button
                                        size='sm'
                                        variant='primary'
                                        className={`rounded-md px-4 ${decision_maker?.isInContactList ? 'bg-blueLight50 text-blueLight700 ' : 'bg-blueGray50 text-blueGray700'}`}
                                        onClick={handleAddToContacts(
                                            decision_maker?.id,
                                            decision_maker?.isInContactList
                                        )}
                                        disabled={addToContactLoading}
                                    >
                                        {decision_maker?.isInContactList ? (
                                            <div className='flex items-center flex-row gap-[2px]'>
                                                <AddedIcon className='text-blueLight700' />
                                                <p>Added</p>
                                            </div>
                                        ) : (
                                            <div className='flex items-center flex-row gap-1'>
                                                <AddIcon className='text-blueGray700' />
                                                <p>Add</p>
                                            </div>
                                        )}
                                    </Button>
                                </TableCell>
                            </TableRow>
                        ))
                    ) : (
                        <TableRow className='border-none'>
                            <TableCell colSpan={5} className='h-[300px] text-center text-gray-400'>
                                No decision maker found
                            </TableCell>
                        </TableRow>
                    )}
                </TableBody>
            </Table>
        </div>
    </div>
);

export { CompanySheetTable };
